import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Link2, Loader2, Sparkles } from "lucide-react";

export default function DuelInviteModal({ open, onOpenChange, attemptId, examTitle }) {
  const [busy, setBusy] = useState(false);
  const [duelUrl, setDuelUrl] = useState(null);
  const [errorPaywall, setErrorPaywall] = useState(false);

  const reset = () => {
    setBusy(false);
    setDuelUrl(null);
    setErrorPaywall(false);
  };

  const create = async () => {
    setBusy(true);
    setErrorPaywall(false);
    try {
      const r = await api.post("/duels/create", { attempt_id: attemptId });
      const code = r.data.duel.code;
      setDuelUrl(`${window.location.origin}/duel/${code}`);
    } catch (e) {
      if (e?.response?.status === 402) setErrorPaywall(true);
      else toast.error(e?.response?.data?.detail || "Could not create duel");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(duelUrl);
      toast.success("Duel link copied — share on WhatsApp");
    } catch {
      toast.error("Copy failed");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Beat me on ExamBro — ${examTitle}`,
          text: `I challenge you on this mock paper. Same questions, same timer.`,
          url: duelUrl,
        });
      } catch (err) {
        // User cancelled the native share dialog — not an error to surface.
        console.debug("share cancelled:", err?.name);
      }
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="duel-invite-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Swords className="h-4 w-4 text-rose-600" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Duel a friend</span>
          </div>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            Challenge someone on this paper
          </DialogTitle>
          <DialogDescription>
            Send the link — they attempt the exact same exam. Results appear side-by-side, instantly shareable.
          </DialogDescription>
        </DialogHeader>

        {!duelUrl && !errorPaywall && (
          <Button
            onClick={create}
            disabled={busy}
            className="w-full bg-rose-600 hover:bg-rose-700 text-white h-11"
            data-testid="duel-create-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Swords className="h-4 w-4 mr-2" />}
            Generate duel link
          </Button>
        )}

        {errorPaywall && (
          <div className="p-4 rounded-md border border-orange-300 bg-orange-50">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-sm text-stone-900">Duel is a Premium feature</div>
                <div className="text-xs text-stone-600 mt-1">
                  You've used your one free duel. Upgrade to Premium to send unlimited challenges.
                </div>
              </div>
            </div>
          </div>
        )}

        {duelUrl && (
          <div className="space-y-3">
            <div className="p-3 rounded-md bg-stone-100 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-stone-500 flex-shrink-0" />
              <Input
                readOnly
                value={duelUrl}
                className="flex-1 bg-transparent border-none font-mono text-xs h-7 px-0 focus-visible:ring-0"
                data-testid="duel-url"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={copy}
                data-testid="duel-copy-btn"
              >
                <Link2 className="h-4 w-4 mr-1" /> Copy link
              </Button>
              <Button
                onClick={share}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="duel-share-btn"
              >
                <Swords className="h-4 w-4 mr-1" /> Share
              </Button>
            </div>
            <p className="text-xs text-stone-500 text-center">
              The result page is public — anyone with the link can see the final scoreboard.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
